import _sortBy from 'lodash/sortBy';
import moment from 'moment';
import jwtDecode from 'jwt-decode';
import { getToken } from 'grab-login/lib/helper';
import { isSameDayDelivery } from 'helper/history-order';
import { parsePaxFetchBusinessProfileSuccessAction } from 'helper/user';
import { isEmpty, isEqualObject } from '../common/utils/data-utils';
import arrayToMap from '../common/utils/array-to-map';
import {
  ADD_BREADCUMB_PIECE,
  INVALID_TOKEN,
  SAVE_CITY,
  FETCH_HISTORY_NOTIFICATION,
  FETCH_INITIAL_USER_CONFIGURATION,
  FETCH_USER_CONFIGURATION,
  FETCH_PAX_CONFIGURATION,
  FETCH_CITY,
  TOGGLE_LOCATION_DRAWER,
  PUSH_NOTIFICATION,
  CLEAR_NOTIFICATION,
  PUSH_ERROR_MODAL,
  CLEAR_ERROR_MODAL,
  TOGGLE_APPBAR,
  SET_COMPANY_AND_GROUP,
  PAX_FETCH_BUSINESS_PROFILES,
  SET_BLOCK_CASHLESS_PAYMENTS,
  SET_FAILED_PAYMENTS_RETRY_STATUS,
  RETRY_FAILED_PAYMENTS,
  LOCATION_DRAWER_ROUTE_OPTIMIZATION,
  TOGGLE_MAINTENANCEBANNER,
} from '../actions/app-actions';
import { FETCH_SERVICE_TYPES } from '../actions/booking-actions';
import {
  GET_TOTAL_COUNT,
  DECREMENT_TOTAL_COUNT,
  FILTER_CANCEL_DELIVERY,
} from '../actions/history-actions';
import {
  FETCH_USER_INFO,
  GET_COMPANY_USER_PROFILES,
  GET_USERNAME_INFO,
} from '../actions/login-actions';
import { ORDER_STATUS } from '../components/history/filter-form/filter-form';
import { ACTIVE_DELIVERY_STATUS } from '../constants/history-constants';

const initialState = {
  breadcumbPieces: [],
  userInfo: {
    user: { safeID: '', name: '' },
  },
  totalCount: 0,
  invalidToken: {
    yes: false,
    anyoneLoggedIn: false,
  },
  city: {},
  historyNotification: {},
  unhappyNotifications: [],
  activeHistoryQty: 0,
  isExceedRetryingFetching: false,
  isFetchingServiceTypeErrors: false,
  numberOfRetryingFetching: 0,
  isRequiredOrderID: false,
  locationDrawer: {
    opening: false,
    allowDrag: false,
    showRouteOptimizationOverlay: false,
  },
  notifications: [],
  appbarVisible: true,
  isCashlessPaymentDisabled: false,
  cashPaymentDisabled: false,
  failedPayments: [],
  failedPaymentsRetryStatus: {
    listFailedPaymentsModalVisible: false,
    retryFailedPaymentsModalVisible: false,
    isLoading: false,
    isError: false,
    isSuccessful: false,
  },
  enableShopifyOrders: false,
  enableMandatoryItemType: [],
  enableFreeTextItemType: [],
  quoteRequestConfig: {
    ajaxTimeoutInMs: 0,
    enableChunking: false,
    chunkSize: 500,
    chunkThrottleTimeInMs: 300,
  },
  geWebMaintenanceBannerConfig: {
    type: 'info',
    dismissible: true,
    startUTCDateTime: 0,
    endUTCDateTime: 0,
    message: '',
    locale: '',
  },
  expressPaxHomeV2AnnouncementBannerConfig: [],
  maintenanceBannerDismissed: false,
};

export const BREADCUMB_ACTION = {
  PUSH: 'PUSH',
  ADD_TAIL: 'ADD_TAIL',
  REPLACE_TAIL: 'REPLACE_TAIL',
};

const AppReducer = (state = initialState, action) => {
  switch (action.type) {
    case FETCH_SERVICE_TYPES.ERROR: {
      return {
        ...state,
        isFetchingServiceTypeErrors: true,
      };
    }
    case FETCH_SERVICE_TYPES.SUCCESS: {
      return {
        ...state,
        isFetchingServiceTypeErrors: false,
      };
    }

    case FETCH_CITY.SUCCESS: {
      return {
        ...state,
        cities: _sortBy(action.data.cities, c => c.name),
      };
    }
    case ADD_BREADCUMB_PIECE: {
      let { breadcumbPieces } = state;

      const { data } = action;
      if (data.action === BREADCUMB_ACTION.PUSH) {
        breadcumbPieces = [data.path];
      } else if (data.action === BREADCUMB_ACTION.ADD_TAIL) {
        breadcumbPieces = breadcumbPieces.concat(data.path);
      } else if (data.action === BREADCUMB_ACTION.REPLACE_TAIL) {
        breadcumbPieces = [].concat(breadcumbPieces.slice(0, -1), [data.path]);
      }
      return { ...state, breadcumbPieces };
    }

    case PAX_FETCH_BUSINESS_PROFILES.SUCCESS: {
      return {
        ...state,
        userInfo: {
          ...state.userInfo,
          ...parsePaxFetchBusinessProfileSuccessAction(action),
        },
      };
    }

    case GET_COMPANY_USER_PROFILES.SUCCESS: {
      return {
        ...state,
        userInfo: { ...state.userInfo, ...action.data.response },
      };
    }

    // TODO: deprecate FETCH_USER_INFO after rollout
    case FETCH_USER_INFO.SUCCESS:
    case GET_USERNAME_INFO.SUCCESS: {
      return { ...state, userInfo: { ...state.userInfo, ...action.data } };
    }

    case FETCH_USER_INFO.ERROR: {
      return state;
    }

    case GET_TOTAL_COUNT.SUCCESS: {
      return {
        ...state,
        isFetchingHistory: false,
        totalCount: action.data.totalCount || 0,
      };
    }

    case DECREMENT_TOTAL_COUNT: {
      return {
        ...state,
        totalCount: state.totalCount - 1,
      };
    }

    case INVALID_TOKEN: {
      const expirationTime = jwtDecode(getToken()).exp * 1000;
      const now = moment();
      const anyoneLoggedIn = moment(expirationTime).isAfter(now);

      return {
        ...state,
        invalidToken: {
          yes: true,
          anyoneLoggedIn,
        },
      };
    }

    case SAVE_CITY: {
      if (isEqualObject(state.city, action.city)) return state;
      const newCityObject = { ...state.city, ...action.city };
      return { ...state, city: newCityObject };
    }

    case FETCH_HISTORY_NOTIFICATION.SUCCESS: {
      const { deliveries } = action.data;
      const oldHistory = state.historyNotification;
      let newActiveHistoryQty = 0;
      if (isEmpty(deliveries)) return state;
      if (isEmpty(oldHistory)) {
        newActiveHistoryQty = deliveries.reduce((acc, delivery) => {
          const isActiveStt = ACTIVE_DELIVERY_STATUS.some(
            activeStt => activeStt === delivery.status,
          );
          return isActiveStt ? acc + 1 : acc;
        }, 0);
        return {
          ...state,
          historyNotification: arrayToMap(deliveries, 'deliveryID'),
          activeHistoryQty: newActiveHistoryQty,
        };
      }
      const newUnhappyNotifications = [];
      deliveries.forEach(delivery => {
        const {
          deliveryID,
          status,
          receiver,
          previousDeliveryID,
          isCurrentUserDelivery,
          service,
          driver,
        } = delivery;
        if (
          status === ORDER_STATUS.CANCELLED &&
          oldHistory[deliveryID] &&
          oldHistory[deliveryID].status !== ORDER_STATUS.CANCELLED
        ) {
          newUnhappyNotifications.push({
            deliveryID,
            status,
            receiver,
          });
        } else if (
          (isCurrentUserDelivery &&
            status === ORDER_STATUS.PICKING_UP &&
            !isEmpty(previousDeliveryID) &&
            deliveryID !== previousDeliveryID &&
            !oldHistory[deliveryID]) ||
          (isCurrentUserDelivery &&
            status === ORDER_STATUS.PICKING_UP &&
            !isEmpty(previousDeliveryID) &&
            oldHistory[deliveryID] &&
            oldHistory[deliveryID].status === ORDER_STATUS.SCHEDULED) ||
          (isCurrentUserDelivery &&
            status === ORDER_STATUS.PICKING_UP &&
            oldHistory[deliveryID].isReallocated &&
            oldHistory[deliveryID] &&
            oldHistory[deliveryID].status === ORDER_STATUS.SCHEDULED)
        ) {
          // if last fetch doesn't include the new delivery id (reallocated) th ten show notification
          newUnhappyNotifications.push({
            deliveryID,
            status,
            receiver,
            service,
          });
        } else if (
          (status === ORDER_STATUS.NO_DRIVER &&
            oldHistory[deliveryID] &&
            oldHistory[deliveryID].status === ORDER_STATUS.SCHEDULED) ||
          (status === ORDER_STATUS.NO_DRIVER &&
            oldHistory[deliveryID] &&
            oldHistory[deliveryID].status === ORDER_STATUS.PICKING_UP)
        ) {
          newUnhappyNotifications.push({
            deliveryID,
            status,
            receiver,
          });
        } else if (
          status === ORDER_STATUS.SCHEDULED &&
          isSameDayDelivery(deliveryID) &&
          oldHistory[deliveryID] &&
          oldHistory[deliveryID].status === ORDER_STATUS.SCHEDULED &&
          !isEmpty(driver) &&
          isEmpty(oldHistory[deliveryID].driver)
        ) {
          if (!isEmpty(delivery.etaMessage)) {
            const { etaMessage } = delivery;
            const { timeWindow } = etaMessage;
            newUnhappyNotifications.push({
              deliveryID,
              status,
              receiver,
              timeWindow,
              service,
            });
          }
        }
        const isActiveStt = ACTIVE_DELIVERY_STATUS.some(
          activeStt => activeStt === status,
        );
        if (isActiveStt) newActiveHistoryQty += 1;
      });
      return {
        ...state,
        historyNotification: arrayToMap(deliveries, 'deliveryID'),
        unhappyNotifications: newUnhappyNotifications,
        activeHistoryQty: newActiveHistoryQty,
        numberOfRetryingFetching: 0,
      };
    }
    case FETCH_HISTORY_NOTIFICATION.ERROR: {
      const { numberOfRetryingFetching } = state;
      if (numberOfRetryingFetching === 5) {
        return {
          ...state,
          isExceedRetryingFetching: true,
        };
      }
      return {
        ...state,
        numberOfRetryingFetching: numberOfRetryingFetching + 1,
        isExceedRetryingFetching: false,
      };
    }

    case FILTER_CANCEL_DELIVERY.SUCCESS: {
      const { deliveryID: canceledDeliveryID } = action.data;
      const { historyNotification, activeHistoryQty } = state;

      if (historyNotification[canceledDeliveryID]) {
        historyNotification[canceledDeliveryID].status = ORDER_STATUS.CANCELLED;
      }

      return {
        ...state,
        historyNotification,
        activeHistoryQty: activeHistoryQty - 1,
      };
    }

    case FETCH_PAX_CONFIGURATION.SUCCESS:
    case FETCH_INITIAL_USER_CONFIGURATION.SUCCESS:
    case FETCH_USER_CONFIGURATION.SUCCESS: {
      const {
        requireOrderId,
        bulkBookingWithExcelUpload,
        cashPaymentDisabled,
        enableCreateDeliveriesV2,
        configuration: {
          enableShopifyOrders,
          enableAnytimeBooking,
          enableWebCancellation,
          enableCancellationRules = false,
          compulsoryCOD = false,
          enableBookingPageRevamp = false,
          showInTransitQRCode = false,
          ongoingBookingRefreshIntervalInMs = 60000,
          itemCategories,
          enableDeliveryGuarantee = false,
          enableMandatoryItemType = ['VN'],
          enableFreeTextItemType = ['VN'],
          quoteRequestConfig = {
            ajaxTimeoutInMs: 0,
            enableChunking: false,
            chunkSize: 500,
            chunkThrottleTimeInMs: 300,
          },
          geWebMaintenanceBannerConfig = {
            type: 'info',
            dismissible: true,
            startUTCDateTime: 0,
            endUTCDateTime: 0,
            message: '',
            locale: '',
          },
          bannerConfigV2 = [],
          enableGetPermissionsEndpoint = false,
          enableGetUserProfilesEndpoint = false,
          enableMSD = false,
          enableExpressWebPaySDK = false,
        },
      } = action.data;
      return {
        ...state,
        compulsoryCOD,
        enableBookingPageRevamp,
        ongoingBookingRefreshIntervalInMs,
        enableCancellationRules,
        showInTransitQRCode,
        itemCategories,
        isRequiredOrderID: requireOrderId || false,
        bulkBookingWithExcelUpload: bulkBookingWithExcelUpload !== false,
        cashPaymentDisabled: cashPaymentDisabled || false,
        enableCreateDeliveriesV2: enableCreateDeliveriesV2 !== false,
        enableShopifyOrders: enableShopifyOrders || false,
        enableAnytimeBookingUIBooking: enableAnytimeBooking || false,
        enableWebCancellation: enableWebCancellation || false,
        enableDeliveryGuarantee,
        enableMandatoryItemType,
        enableFreeTextItemType,
        quoteRequestConfig,
        geWebMaintenanceBannerConfig,
        bannerConfigV2,
        enableGetPermissionsEndpoint,
        enableGetUserProfilesEndpoint,
        enableMSD,
        enableExpressWebPaySDK,
      };
    }

    case TOGGLE_LOCATION_DRAWER: {
      const { locations, isSender, allowDrag } = action.payload;
      const { opening } = state.locationDrawer;

      return {
        ...state,
        locationDrawer: {
          locations,
          isSender: opening
            ? state.locationDrawer.isSender // keep previous state in case closing/confirm the drawer, icon wont be switch to drop-off
            : isSender,
          opening: !opening,
          allowDrag,
        },
      };
    }

    case TOGGLE_APPBAR: {
      return {
        ...state,
        appbarVisible: !state.appbarVisible,
      };
    }

    case TOGGLE_MAINTENANCEBANNER: {
      return {
        ...state,
        maintenanceBannerDismissed: action.payload,
      };
    }

    case PUSH_NOTIFICATION: {
      return {
        ...state,
        notifications: state.notifications.concat(action.payload),
      };
    }

    case CLEAR_NOTIFICATION: {
      return {
        ...state,
        notifications: [],
      };
    }

    case PUSH_ERROR_MODAL: {
      return {
        ...state,
        errorModal: action.payload,
      };
    }

    case CLEAR_ERROR_MODAL: {
      return {
        ...state,
        errorModal: {},
      };
    }

    case SET_COMPANY_AND_GROUP: {
      const { company, group } = action.payload;
      return {
        ...state,
        userInfo: {
          ...state.userInfo,
          currentCompany: company,
          currentGroup: group,
        },
      };
    }

    case SET_BLOCK_CASHLESS_PAYMENTS: {
      return {
        ...state,
        ...action.payload,
      };
    }
    case SET_FAILED_PAYMENTS_RETRY_STATUS: {
      return {
        ...state,
        failedPaymentsRetryStatus: {
          ...state.failedPaymentsRetryStatus,
          ...action.payload,
        },
      };
    }

    case RETRY_FAILED_PAYMENTS.START: {
      return {
        ...state,
        failedPaymentsRetryStatus: {
          ...state.failedPaymentsRetryStatus,
          isLoading: true,
          isError: false,
          isSuccessful: false,
        },
      };
    }

    case RETRY_FAILED_PAYMENTS.ERROR: {
      return {
        ...state,
        failedPaymentsRetryStatus: {
          ...state.failedPaymentsRetryStatus,
          isLoading: false,
          isError: true,
          isSuccessful: false,
        },
      };
    }
    case RETRY_FAILED_PAYMENTS.SUCCESS: {
      return {
        ...state,
        failedPaymentsRetryStatus: {
          ...state.failedPaymentsRetryStatus,
          isLoading: false,
          isError: false,
          isSuccessful: true,
        },
      };
    }
    case LOCATION_DRAWER_ROUTE_OPTIMIZATION.START: {
      const locationDrawer = state.locationDrawer;
      return {
        ...state,
        locationDrawer: {
          ...locationDrawer,
          showRouteOptimizationOverlay: true,
        },
      };
    }
    case LOCATION_DRAWER_ROUTE_OPTIMIZATION.ERROR: {
      const locationDrawer = state.locationDrawer;
      return {
        ...state,
        locationDrawer: {
          ...locationDrawer,
          showRouteOptimizationOverlay: false,
        },
      };
    }
    case LOCATION_DRAWER_ROUTE_OPTIMIZATION.SUCCESS: {
      const locationDrawer = state.locationDrawer;
      return {
        ...state,
        locationDrawer: {
          ...locationDrawer,
          showRouteOptimizationOverlay: false,
        },
      };
    }

    default:
      return state;
  }
};

export default AppReducer;
